import React, {Component} from 'react';
import { Link, withRouter } from 'react-router-dom';
import { message } from 'antd';
import { addtocart, addwishlist, removewishlist } from '../../utils/byoscart';
import { wishlistApi } from '../../api/Api';

class ProductItem extends Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedSkuId: this.getInitialSkuId(props.product_details || {}),
            addingToCart: false,
            addedSkuIds: {},
            wishlistSkuIds: {},
            brokenBadgeIcons: {},
        };
    }

    componentDidUpdate(prevProps) {
        const previousProduct = prevProps.product_details || {};
        const product = this.props.product_details || {};
        const previousKey = previousProduct.product_id || previousProduct.id;
        const nextKey = product.product_id || product.id;

        if (previousKey !== nextKey) {
            this.setState({ selectedSkuId: this.getInitialSkuId(product), addedSkuIds: {}, brokenBadgeIcons: {} });
        }
    }

    componentDidMount() {
        if (localStorage.getItem('natureway_token')) {
            wishlistApi.list({ optional: true }).then((response) => {
                const saved = response.data?.data || [];
                const savedIds = saved.reduce((ids, item) => {
                    ids[item.product_sku_id] = true;
                    return ids;
                }, {});
                this.setState({ wishlistSkuIds: savedIds });
            }).catch(() => {});
        }
    }

    getProduct() {
        return this.props.product_details || {};
    }

    getSkus(product) {
        return Array.isArray(product.skus) ? product.skus : [];
    }

    getInitialSkuId(product) {
        const skus = this.getSkus(product);
        const defaultSku = skus.find((sku) => sku.is_default) || skus[0] || product.default_sku || {};
        return defaultSku.id || product.variation_id || product.product_sku_id || product.id || '';
    }

    getSelectedSku(product) {
        const skus = this.getSkus(product);
        return skus.find((sku) => String(sku.id) === String(this.state.selectedSkuId)) || skus[0] || product.default_sku || {};
    }

    getProductImage(product) {
        return product.display_image || product.product_image || product.image_path || '/assets/img/logo-no-image.png';
    }

    getProductLink(product) {
        const slug = product.slug || product.product_slug;
        if (slug) {
            return `/product/${encodeURIComponent(slug)}`;
        }

        const name = product.product_name || product.name || 'Product';
        return `/product/${encodeURIComponent(String(product.id || product.product_id || name))}`;
    }

    getProductId(product) {
        return product.product_id || product.id;
    }

    getVariantId(product) {
        const selectedSku = this.getSelectedSku(product);
        return selectedSku.id || product.product_sku_id || product.variation_id || product.variant_id || product.sku_id || product.id;
    }

    getCardCopy(product) {
        const title = (product.product_name || 'Product').trim();
        const subtitle = (
            product.product_subtitle ||
            product.short_description ||
            product.category_name ||
            product.brand_name ||
            product.tagline ||
            ''
        ).trim();

        return {title, subtitle};
    }

    getPrice(product, selectedSku = {}) {
        const pricing = selectedSku.pricing || product.pricing || {};
        const offer = selectedSku.offer || pricing.offer || product.offer || {};
        const hasOffer = Boolean(offer.has_offer);
        const mrp = pricing.mrp || selectedSku.mrp || product.mrp || '';
        const sale = hasOffer
            ? (pricing.offer_price || offer.offer_price || pricing.selling_price || product.offer_price || '')
            : (pricing.selling_price || product.offer_price || '');
        const regular = mrp;
        const discount = this.getEffectiveDiscountPercent(regular, sale) || pricing.discount_percent || offer.discount_percent || product.discount_percent || '';
        return {
            sale,
            regular,
            discount,
            badge: offer.state==='conditional' ? offer.offer_badge : discount ? `${Math.round(Number(discount))}% OFF` : '',
            currency: pricing.currency_symbol || '₹',
        };
    }

    shouldShowMrp(regular, sale) {
        return Number(regular || 0) > Number(sale || 0);
    }

    getEffectiveDiscountPercent(regular, sale) {
        const mrp = Number(regular || 0);
        const finalPrice = Number(sale || 0);

        if (mrp <= 0 || finalPrice <= 0 || finalPrice >= mrp) {
            return 0;
        }

        return ((mrp - finalPrice) / mrp) * 100;
    }

    getStock(product, selectedSku = {}) {
        if (selectedSku.inventory && selectedSku.inventory.in_stock !== undefined) {
            return selectedSku.inventory.in_stock;
        }
        if (product.inventory && product.inventory.in_stock !== undefined) {
            return product.inventory.in_stock;
        }
        if (product.in_stock !== undefined) {
            return product.in_stock;
        }
        return true;
    }

    getTopBadge(product) {
        const badges = Array.isArray(product.badges) ? product.badges : [];
        const badge = badges.find((item) => item && (item.name || item.title || item.label));

        if (badge) {
            const adminIcon = badge.icon || '';
            const imageIcon = this.isImageIcon(adminIcon) ? (badge.icon_url || adminIcon) : '';

            return {
                label: badge.name || badge.title || badge.label,
                icon: imageIcon || adminIcon || badge.icon_url || '',
            };
        }

        const label = product.badge_name || product.badge || product.product_badge || '';

        return label ? { label, icon: product.badge_icon || product.badge_icon_path || '' } : null;
    }

    getBadgeIconKey(topBadge) {
        return `${topBadge.label || ''}:${topBadge.icon || ''}`;
    }

    isImageIcon(icon) {
        const value = String(icon || '').trim();

        return /^https?:\/\//.test(value) ||
            value.startsWith('/') ||
            /\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i.test(value);
    }

    isSvgMarkup(icon) {
        return /^<svg[\s>]/i.test(String(icon || '').trim());
    }

    sanitizeSvgMarkup(icon) {
        return String(icon || '')
            .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
            .replace(/\son[a-z]+\s*=\s*(['"]).*?\1/gi, '')
            .replace(/\s(href|xlink:href)\s*=\s*(['"])\s*javascript:.*?\2/gi, '');
    }

    getBadgeIconClass(topBadge) {
        const icon = String(topBadge.icon || '').trim();

        if (icon && !this.isImageIcon(icon)) {
            return icon.includes(' ') ? icon : `fa fa-${icon}`;
        }

        const label = String(topBadge.label || '').toLowerCase();

        if (label.includes('organic') || label.includes('natural')) {
            return 'fa fa-leaf';
        }

        if (label.includes('new')) {
            return 'fa fa-certificate';
        }

        return 'fa fa-tag';
    }

    renderTopBadgeIcon(topBadge) {
        const iconKey = this.getBadgeIconKey(topBadge);

        if (this.isSvgMarkup(topBadge.icon)) {
            return (
                <span
                    className="ps-product-item__topBadgeSvg"
                    aria-hidden="true"
                    dangerouslySetInnerHTML={{ __html: this.sanitizeSvgMarkup(topBadge.icon) }}
                />
            );
        }

        if (topBadge.icon && this.isImageIcon(topBadge.icon) && !this.state.brokenBadgeIcons[iconKey]) {
            return (
                <img
                    src={topBadge.icon}
                    alt=""
                    aria-hidden="true"
                    onError={() => this.setState((state) => ({
                        brokenBadgeIcons: {
                            ...state.brokenBadgeIcons,
                            [iconKey]: true,
                        },
                    }))}
                />
            );
        }

        return <i className={this.getBadgeIconClass(topBadge)} aria-hidden="true"></i>;
    }

    getRating(product) {
        const suppliedRating = [
            product.rating,
            product.rattings,
            product.average_rating,
            product.review_rating,
        ].find((rating) => rating !== undefined && rating !== null && rating !== '');
        const value = Number(suppliedRating !== undefined ? suppliedRating : 0);
        if (Number.isNaN(value)) {
            return 0;
        }
        return Math.max(0, Math.min(5, value));
    }

    getReviewCount(product) {
        const count = Number(product.review_count || product.total_reviews || product.total_review || product.reviewCount || 0);
        return Number.isNaN(count) ? 0 : count;
    }

    getIconType(product, title) {
        const text = `${title || ''} ${product.category_name || ''} ${product.brand_name || ''}`.toLowerCase();

        if ((product.icon_type || '').toLowerCase() === 'person') return 'person';
        if ((product.icon_type || '').toLowerCase() === 'lamp') return 'lamp';
        if ((product.icon_type || '').toLowerCase() === 'gift') return 'gift';

        if (text.includes('hair') || text.includes('skin')) return 'person';
        if (text.includes('pooja') || text.includes('lamp') || text.includes('diya')) return 'lamp';
        if (text.includes('combo') || text.includes('gift')) return 'gift';
        return 'bottle';
    }

    renderBadgeIcon(product, title) {
        const iconPath = (product.icon_path || product.icon_url || product.badge_icon_path || '').trim();
        if (iconPath) {
            return <img src={iconPath} alt="" aria-hidden="true" />;
        }

        const iconType = this.getIconType(product, title);

        switch (iconType) {
        case 'person':
            return (
                <svg viewBox="0 0 64 64" aria-hidden="true" focusable="false">
                    <path d="M43 20.5c0 6-4.9 10.9-11 10.9S21 26.5 21 20.5 25.9 9.6 32 9.6s11 4.9 11 10.9Z" />
                    <path d="M15 53c2.9-11.1 10.4-16.7 17-16.7S46.1 41.9 49 53" />
                    <path d="M33.5 24.8c-2.8-.4-5.4-2.4-6.8-5.3" />
                    <path d="M37.2 24.8c2.8-.4 5.4-2.4 6.8-5.3" />
                    <path d="M26.7 12.8c1.3 2.2 3.2 3.6 5.3 4.1" />
                </svg>
            );
        case 'lamp':
            return (
                <svg viewBox="0 0 64 64" aria-hidden="true" focusable="false">
                    <path d="M32 10c6.2 6.8 10 12.3 10 18.7 0 6.7-4.5 11.3-10 11.3s-10-4.6-10-11.3C22 22.3 25.8 16.8 32 10Z" />
                    <path d="M22 42h20" />
                    <path d="M24 50h16" />
                    <path d="M32 29v20" />
                    <path d="M27 35c2.1 1.8 4.2 2.7 5 2.7s2.9-.9 5-2.7" />
                </svg>
            );
        case 'gift':
            return (
                <svg viewBox="0 0 64 64" aria-hidden="true" focusable="false">
                    <path d="M14 26h36v28H14z" />
                    <path d="M32 26v28" />
                    <path d="M14 36h36" />
                    <path d="M32 26c-2.6-7.3-9.3-12-15-12-3.4 0-6 2.5-6 5.7 0 6.1 7.8 11.3 21 11.3" />
                    <path d="M32 26c2.6-7.3 9.3-12 15-12 3.4 0 6 2.5 6 5.7 0 6.1-7.8 11.3-21 11.3" />
                </svg>
            );
        case 'bottle':
        default:
            return (
                <svg viewBox="0 0 64 64" aria-hidden="true" focusable="false">
                    <path d="M26 10h12" />
                    <path d="M28 10v8l-5 6v24c0 5.5 4.5 10 9 10s9-4.5 9-10V24l-5-6v-8" />
                    <path d="M24 26h16" />
                    <path d="M32 30v20" />
                    <path d="M22 48c3.3 2.2 6.7 3.3 10 3.3S38.7 50.2 42 48" />
                </svg>
            );
        }
    }

    renderStars(rating) {
        return Array.from({length: 5}).map((_, index) => {
            const threshold = index + 1;
            const isActive = rating >= threshold;
            const isHalf = !isActive && rating > index && rating < threshold;

            return (
                <span
                    className={`ps-product-item__star ${isActive ? 'ps-product-item__star--active' : ''} ${isHalf ? 'ps-product-item__star--half' : ''}`}
                    key={`star-${index}`}
                    aria-hidden="true"
                >
                    <i className={`fa ${isHalf ? 'fa-star-half-o' : (isActive ? 'fa-star' : 'fa-star-o')}`}></i>
                </span>
            );
        });
    }

    handleAddToCart = async (event, product, inStock) => {
        event.preventDefault();
        event.stopPropagation();

        if (this.state.addingToCart) {
            return;
        }

        if (!inStock) {
            message.error({
                content: 'This product is currently out of stock.',
                className: 'notificationBox',
            });
            return;
        }

        const productId = this.getProductId(product);
        const variantId = this.getVariantId(product);

        if (!productId || !variantId) {
            message.error({
                content: 'Product variant is not available.',
                className: 'notificationBox',
            });
            return;
        }

        this.setState({ addingToCart: true });
        try {
            const result = await addtocart(productId, variantId, 1);
            if (result && result.status === 'success') {
                this.setState((state) => ({
                    addedSkuIds: {
                        ...state.addedSkuIds,
                        [variantId]: true,
                    },
                }));
            }
        } finally {
            this.setState({ addingToCart: false });
        }
    }

    handleWishlist = async (event, product) => {
        event.preventDefault();
        event.stopPropagation();

        if (!localStorage.getItem('natureway_token')) {
            this.props.history.push({ pathname: '/signin', state: {
                from: this.props.location,
            } });
            return;
        }

        const variantId = this.getVariantId(product);
        if (variantId) {
            const isSaved = Boolean(this.state.wishlistSkuIds[variantId]);
            this.setState((state) => ({ wishlistSkuIds: { ...state.wishlistSkuIds, [variantId]: !isSaved } }));
            const result = isSaved ? await removewishlist(variantId) : await addwishlist(variantId);
            if (!result || result.status !== 'success') {
                this.setState((state) => ({ wishlistSkuIds: { ...state.wishlistSkuIds, [variantId]: isSaved } }));
            }
        }
    }

    handleVariantChange = (event) => {
        this.setState({ selectedSkuId: event.target.value });
    }

    getVariantLabel(sku) {
        if (Array.isArray(sku.variant_details) && sku.variant_details.length > 0) {
            return sku.variant_details
                .map((variant) => variant.item_value || variant.item_name)
                .filter(Boolean)
                .join('-');
        }

        if (sku.variant_name) {
            return sku.variant_name
                .split(',')
                .map((part) => part.includes(':') ? part.split(':').slice(1).join(':').trim() : part.trim())
                .filter(Boolean)
                .join('-');
        }

        return sku.sku || `Variant ${sku.id}`;
    }

    render() {
        const product = this.getProduct();
        const {title} = this.getCardCopy(product);
        const image = this.getProductImage(product);
        const link = this.getProductLink(product);
        const selectedSku = this.getSelectedSku(product);
        const skus = this.getSkus(product);
        const {sale, regular, badge, currency} = this.getPrice(product, selectedSku);
        const rating = this.getRating(product);
        const reviewCount = this.getReviewCount(product);
        const inStock = this.getStock(product, selectedSku);
        const topBadge = this.getTopBadge(product);
        const variantId = this.getVariantId(product);
        const isAddedToCart = Boolean(this.state.addedSkuIds[variantId]);

        return (
            <article className="ps-product-item">
                <div className="ps-product-item__link">
                    <Link className="ps-product-item__mediaLink" to={link} aria-label={title}>
                    <div className="ps-product-item__media">
                        {badge ? <span className="ps-product-item__discount">{badge}</span> : null}
                        {topBadge ? (
                            <span className="ps-product-item__topBadge">
                                {this.renderTopBadgeIcon(topBadge)}
                                {topBadge.label}
                            </span>
                        ) : null}
                        <img className="ps-product-item__image" src={image} alt={title} loading="lazy" />
                    </div>
                    </Link>

                    <div className="ps-product-item__content">
                        <h5 className="ps-product-item__title">
                            <Link to={link}>{title}</Link>
                        </h5>

                        <div className="ps-product-item__rating" aria-label={`${rating.toFixed(1)} out of 5 stars`}>
                            <div className="ps-product-item__stars">{this.renderStars(rating)}</div>
                            <span className="ps-product-item__reviewCount">({reviewCount})</span>
                        </div>

                        <div className="ps-product-item__priceRow">
                            {sale ? <span className="ps-product-item__price">{currency}{sale}</span> : null}
                            {this.shouldShowMrp(regular, sale) ? <span className="ps-product-item__strike">{currency}{regular}</span> : null}
                            {this.props.showPriceOffer && this.shouldShowMrp(regular, sale) && badge ? <span className="ps-product-item__price-offer">{badge}</span> : null}
                        </div>

                        {skus.length > 1 ? (
                            <label className="ps-product-item__variantSelect" aria-label="Select variant">
                                <select value={this.state.selectedSkuId} onChange={this.handleVariantChange}>
                                    {skus.map((sku) => (
                                        <option value={sku.id} key={sku.id}>
                                            {this.getVariantLabel(sku)}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        ) : skus.length === 1 ? (
                            <div className="ps-product-item__variantPill">{this.getVariantLabel(skus[0])}</div>
                        ) : null}

                        <div className="ps-product-item__actions">
                            <button
                                type="button"
                                className={`ps-product-item__button ${isAddedToCart ? 'ps-product-item__button--added' : ''}`}
                                onClick={(event) => this.handleAddToCart(event, product, inStock)}
                                disabled={!inStock || this.state.addingToCart}
                            >
                                {this.state.addingToCart ? (
                                    <span className="ps-product-item__buttonSpinner" aria-hidden="true"></span>
                                ) : isAddedToCart ? (
                                    <i className="fa fa-check" aria-hidden="true"></i>
                                ) : (
                                    <i className="fa fa-shopping-cart" aria-hidden="true"></i>
                                )}
                                {this.state.addingToCart ? 'Adding...' : (inStock ? (isAddedToCart ? 'Added to Cart' : 'Add to Cart') : 'Out of Stock')}
                            </button>
                            <button
                                type="button"
                                className="ps-product-item__wishlist"
                                onClick={(event) => this.handleWishlist(event, product)}
                                aria-label={`${this.state.wishlistSkuIds[variantId] ? 'Remove' : 'Add'} ${title} ${this.state.wishlistSkuIds[variantId] ? 'from' : 'to'} wishlist`}
                            >
                                <i className={`fa ${this.state.wishlistSkuIds[variantId] ? 'fa-heart' : 'fa-heart-o'}`} aria-hidden="true"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </article>
        );
    }
}

export default withRouter(ProductItem);
